export interface RumErrorInput {
    customErrorType?: string;
    isPageFailure: boolean;
    context?: Record<string, unknown>;
    sanitizedMessage?: string;
}

export interface RumLike {
    addError?: (error: unknown, context?: Record<string, unknown>) => void;
}

export const getRumErrorMessage = (error: unknown, fallbackMessage: string): string => {
    if (error instanceof Error) {
        return error.message;
    }

    return typeof error === 'string' ? error : fallbackMessage;
};

const isApolloServerResponseError = (error: unknown): boolean => {
    if (typeof error !== 'object' || error === null) return false;

    const name = 'name' in error && typeof error.name === 'string' ? error.name : '';
    const hasStatusCode = 'statusCode' in error && typeof error.statusCode === 'number';
    const hasResponseStatus =
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'status' in error.response &&
        typeof error.response.status === 'number';

    return (
        name === 'ServerError' || name === 'ServerParseError' || hasStatusCode || hasResponseStatus
    );
};

export const isNetworkError = (error: unknown): boolean => {
    if (typeof error === 'object' && error !== null) {
        if (
            'graphQLErrors' in error &&
            Array.isArray(error.graphQLErrors) &&
            error.graphQLErrors.length > 0
        ) {
            return false;
        }
        if ('networkError' in error && Boolean(error.networkError)) {
            return !isApolloServerResponseError(error.networkError);
        }
    }

    const message = getRumErrorMessage(error, '').toLowerCase();
    const isBrowserFetchFailure =
        error instanceof TypeError &&
        (message.includes('failed to fetch') ||
            message.includes('failed to load') ||
            message.includes('load failed'));

    return (
        isBrowserFetchFailure ||
        message.includes('network error') ||
        message.includes('networkerror')
    );
};

/**
 * Reports a feature error and lets the host aggregate page failures.
 * Use sanitizedMessage when the source error may contain user or server data.
 */
export const logRumError = (
    rum: RumLike | undefined,
    error: unknown,
    { customErrorType, isPageFailure, context, sanitizedMessage }: RumErrorInput,
): void => {
    const message = getRumErrorMessage(error, customErrorType ?? 'Error');
    let reportedError: Error;
    if (sanitizedMessage !== undefined) {
        reportedError = new Error(
            sanitizedMessage.trim().length > 0 ? sanitizedMessage : (customErrorType ?? 'Error'),
        );
    } else if (error instanceof Error) {
        reportedError = error;
    } else {
        reportedError = new Error(message);
    }
    try {
        rum?.addError?.(reportedError, {
            ...context,
            ...(customErrorType ? { customErrorType } : {}),
            pageFailure: isPageFailure,
        });
    } catch (telemetryError) {
        console.error('[DatadogRUM] Failed to report feature error', telemetryError);
    }
};
